import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { API, useAuth } from '@/App';
import { Car } from 'lucide-react';

const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, formData);
      login(response.data.access_token, response.data.user);
      toast.success('Login successful!');
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <Link to="/" className="flex items-center gap-2 mb-8" data-testid="login-logo">
            <Car className="w-8 h-8 text-primary" />
            <span className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Cabinet Grotesk' }}>
              Amjed Tours
            </span>
          </Link>

          <h1 className="text-4xl font-bold mb-2" style={{ fontFamily: 'Cabinet Grotesk' }} data-testid="login-title">
            Welcome Back
          </h1>
          <p className="text-muted-foreground mb-8">Login to your account to continue</p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="your@email.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                data-testid="login-email-input"
              />
            </div>

            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                data-testid="login-password-input"
              />
            </div>

            <Button
              type="submit"
              className="w-full btn-primary"
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading ? 'Logging in...' : 'Login'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-muted-foreground">
              Don't have an account?{' '}
              <Link to="/register" className="text-primary font-medium hover:underline" data-testid="login-register-link">
                Sign up
              </Link>
            </p>
          </div>

          <div className="mt-8 p-4 bg-muted rounded-lg">
            <p className="text-sm font-medium mb-2">Test Credentials:</p>
            <p className="text-xs text-muted-foreground">User: user@test.com / user123</p>
            <p className="text-xs text-muted-foreground">Admin: admin@carrental.pk / admin123</p>
          </div>
        </div>
      </div>

      {/* Right Side - Image */}
      <div className="hidden lg:block flex-1 relative">
        <img
          src="https://images.unsplash.com/photo-1770899235413-3c75535b018a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwyfHxwYWtpc3RhbiUyMGthcmFrb3JhbSUyMGhpZ2h3YXl8ZW58MHx8fHwxNzg0MDY0ODgwfDA&ixlib=rb-4.1.0&q=85"
          alt="Hero"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-primary/90 to-primary/50 flex items-center justify-center text-white p-12">
          <div>
            <h2 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Cabinet Grotesk' }}>
              Start Your Journey Today
            </h2>
            <p className="text-lg opacity-90">
              Explore Pakistan with our premium fleet of vehicles
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
